import { Fragment } from 'react';
import type React from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import EmptyState from '../../../components/feedback/EmptyState';
import PageSection from '../../../components/layout/PageSection';
import { getContrastColor } from '../../../shared/colorUtils';
import type { CaseScheduleEntry } from '../types';

interface CaseScheduleTabProps {
  scheduleLoading: boolean;
  filteredSchedule: CaseScheduleEntry[];
  scheduleFilter: string;
  setScheduleFilter: React.Dispatch<React.SetStateAction<string>>;
  expandedProjectId: number | string | null;
  setExpandedProjectId: React.Dispatch<React.SetStateAction<number | string | null>>;
  today: Date;
  unitStatusColors: Record<string, string>;
}

const FILTERS = ['upcoming', 'active', 'all'] as const;

const CaseScheduleTab = ({
  scheduleLoading,
  filteredSchedule,
  scheduleFilter,
  setScheduleFilter,
  expandedProjectId,
  setExpandedProjectId,
  today,
  unitStatusColors,
}: CaseScheduleTabProps) => {
  const { t } = useTranslation();

  return (
    <PageSection
      title={t('warehouses.cases.schedule')}
      description={t('warehouses.cases.scheduleDescription', 'Review active and upcoming projects that reserve or consume this case.')}
      actions={
        <div className="btn-group" role="group">
          {FILTERS.map((filterValue) => (
            <button
              key={filterValue}
              type="button"
              className={`btn btn-sm ${scheduleFilter === filterValue ? 'btn-primary' : 'btn-outline-secondary'}`}
              onClick={() => setScheduleFilter(filterValue)}
            >
              {filterValue === 'upcoming'
                ? t('warehouses.items.scheduleUpcoming')
                : filterValue === 'active'
                  ? t('warehouses.items.scheduleActive')
                  : t('warehouses.items.scheduleAll')}
            </button>
          ))}
        </div>
      }
      bodyClassName="p-0"
    >
      {scheduleLoading ? (
        <div className="p-3 text-center text-muted">{t('common.loading')}</div>
      ) : filteredSchedule.length === 0 ? (
        <EmptyState
          className="py-5"
          icon={<i className="ti ti-calendar-off app-empty-state__icon-glyph" aria-hidden="true" />}
          title={
            scheduleFilter === 'active'
              ? t('warehouses.items.scheduleNoneActive')
              : scheduleFilter === 'upcoming'
                ? t('warehouses.items.scheduleNoneUpcoming')
                : t('warehouses.cases.scheduleEmpty')
          }
          message={t('warehouses.cases.scheduleEmptyMessage', 'No matching bookings are linked to this case for the selected time filter.')}
        />
      ) : (
        <div className="table-responsive">
          <table className="table table-vcenter card-table app-table mb-0">
            <thead>
              <tr>
                <th style={{ width: 28 }}></th>
                <th>{t('warehouses.cases.scheduleProject')}</th>
                <th>{t('projects.startDate')}</th>
                <th>{t('projects.endDate')}</th>
                <th>{t('warehouses.cases.scheduleQty')}</th>
                <th>{t('projects.status')}</th>
              </tr>
            </thead>
            <tbody>
              {filteredSchedule.map((entry) => {
                const start = entry.start_date ? new Date(entry.start_date) : null;
                const end = entry.end_date ? new Date(entry.end_date) : null;
                const isPast = Boolean(end && end < today);
                const isCurrent = Boolean(start && end && start <= today && end >= today);
                const isExpanded = expandedProjectId === entry.entry_key;
                const contents = entry.contents ?? [];
                const hasPacked = contents.length > 0;

                return (
                  <Fragment key={entry.entry_key}>
                    <tr className={isPast ? 'opacity-50' : ''}>
                      <td>
                        {hasPacked && (
                          <button
                            className="btn btn-sm btn-ghost-secondary p-0"
                            style={{ width: 22, height: 22, lineHeight: 1 }}
                            onClick={() => setExpandedProjectId(isExpanded ? null : entry.entry_key)}
                            title={isExpanded ? t('common.collapse', 'Collapse') : t('common.expand', 'Expand')}
                          >
                            <i className={`ti ${isExpanded ? 'ti-chevron-up' : 'ti-chevron-down'}`} aria-hidden="true" />
                          </button>
                        )}
                      </td>
                      <td>
                        <Link to={entry.link} className={isPast ? 'text-muted' : 'fw-medium'}>
                          {entry.project_name}
                        </Link>
                        {isCurrent && (
                          <span className="badge bg-green ms-2 fw-normal" style={{ fontSize: '0.7rem' }}>
                            {t('warehouses.cases.scheduleNow')}
                          </span>
                        )}
                        {hasPacked && (
                          <span className="badge bg-azure-lt text-azure ms-2 fw-normal" style={{ fontSize: '0.7rem' }}>
                            {contents.length} {t('warehouses.cases.scheduleContents')}
                          </span>
                        )}
                      </td>
                      <td className="text-nowrap">{entry.start_date || <span className="text-muted">—</span>}</td>
                      <td className="text-nowrap">{entry.end_date || <span className="text-muted">—</span>}</td>
                      <td>{entry.quantity}×</td>
                      <td>
                        <span
                          className="badge"
                          style={{
                            backgroundColor: entry.status_color || 'var(--tblr-secondary)',
                            color: getContrastColor(entry.status_color || 'var(--tblr-secondary)'),
                          }}
                        >
                          {entry.status}
                        </span>
                      </td>
                    </tr>
                    {isExpanded && hasPacked && (
                      <tr>
                        <td></td>
                        <td colSpan={5} className="py-2 ps-3">
                          <table
                            className="table table-sm table-vcenter mb-0"
                            style={{ background: 'var(--tblr-bg-surface-secondary, #f8f9fa)', borderRadius: 4 }}
                          >
                            <thead>
                              <tr>
                                <th className="text-muted fw-normal small">{t('warehouses.items.name')}</th>
                                <th className="text-muted fw-normal small">{t('warehouses.units.serialNumber')}</th>
                                <th className="text-muted fw-normal small">{t('warehouses.units.qrCode')}</th>
                                <th className="text-muted fw-normal small">{t('warehouses.cases.scheduleQty')}</th>
                                <th className="text-muted fw-normal small">{t('warehouses.units.status')}</th>
                              </tr>
                            </thead>
                            <tbody>
                              {contents.map((row) => {
                                const statusColor = (row.status && unitStatusColors[row.status]) || 'var(--tblr-secondary)';
                                return (
                                  <tr key={row.id}>
                                    <td className="small fw-medium">{row.item_name}</td>
                                    <td className="small">{row.serial_number || <span className="text-muted">—</span>}</td>
                                    <td>{row.qr_code ? <code className="small">{row.qr_code}</code> : <span className="text-muted">—</span>}</td>
                                    <td className="small">{row.quantity}×</td>
                                    <td>
                                      {row.status ? (
                                        <span className="badge" style={{ backgroundColor: statusColor, color: getContrastColor(statusColor) }}>
                                          {t(`warehouses.units.status_${row.status}`, row.status)}
                                        </span>
                                      ) : <span className="text-muted">—</span>}
                                    </td>
                                  </tr>
                                );
                              })}
                            </tbody>
                          </table>
                        </td>
                      </tr>
                    )}
                  </Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </PageSection>
  );
};

export default CaseScheduleTab;
